import { useState } from "react";
import { MapPin, Monitor, Calendar, User } from "lucide-react";
import { Button } from "@components/shadcn-ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/shadcn-ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@components/shadcn-ui/sheet";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@components/shadcn-ui/drawer";
import { LoginEvent } from "./types";
import { useIsMobile as useMobile } from "@customHooks/use-mobile";
import { toTitleCase } from "features/createApplication/utils";
import TableShimmer, {
  CardShimmer,
} from "features/applications/ApplicationShimmer";

interface LoginLocationsProps {
  loginData: LoginEvent[];
  isLoading: boolean;
}

export function LoginLocations({ loginData, isLoading }: LoginLocationsProps) {
  const isMobile = useMobile();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    mapUrl: string;
    login: LoginEvent;
  } | null>(null);

  const handleViewMap = (login: LoginEvent) => {
    const mapUrl =
      `https://www.openstreetmap.org/export/embed.html` +
      `?bbox=${encodeURIComponent(
        `${Number(login.location.longitude) - 0.005},${
          Number(login.location.latitude) - 0.005
        },${Number(login.location.longitude) + 0.005},${
          Number(login.location.latitude) + 0.005
        }`
      )}` +
      `&layer=mapnik` +
      `&marker=${encodeURIComponent(
        `${login.location.latitude},${login.location.longitude}`
      )}`;

    setSelectedLocation({ mapUrl, login });
    setIsDrawerOpen(true);
  };

  if (isLoading) {
    if (isMobile) {
      return <CardShimmer />;
    }
    return <TableShimmer />;
  }

  return (
    <>
      {/* desktop view */}
      <div className="hidden md:block">
        <Table>
          <TableHeader className="bg-blue-200 rounded-lg">
            <TableRow>
              <TableHead className="p-1 pl-6">User</TableHead>
              {/* <TableHead className="p-1 pl-6">Location</TableHead> */}
              <TableHead className="p-1 pl-6">Device Info</TableHead>
              <TableHead className="p-1 pl-6">Time</TableHead>
              <TableHead className="p-1 pl-6">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-white">
            {loginData.map((login) => (
              <TableRow key={login.eventId}>
                <TableCell className="p-1 pl-6 font-semibold">
                  {toTitleCase(login.userId)}
                </TableCell>
                <TableCell className="p-1 pl-6">
                  <div className="flex items-center">
                    <Monitor className="w-5 h-5 text-green-500 mr-2" />
                    <div>
                      <p className="text-gray-900">
                        {login.metadata.browser}
                        <span className="text-gray-500">
                          {" "}
                          ({login.metadata.device})
                        </span>
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="p-1 pl-6">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-purple-500 mr-2" />
                    <span className="text-gray-900">
                      {new Date(login.eventTimeStamp)
                        .toLocaleString([], {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                        .replace(" at ", ", ")}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="p-1 pl-6">
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      onClick={() => handleViewMap(login)}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      View in Map
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden my-4 gap-2 flex flex-col">
        <div className="text-md text-gray-900 mb-2 font-bold">
          Total Logins: {loginData.length}
        </div>
        {loginData.map((login) => (
          <div
            key={login.eventId}
            className="bg-white rounded-lg shadow p-4 flex flex-col gap-2"
          >
            <div className="flex items-center ">
              <p className="text-gray-500 flex items-center gap-2 text-sm">
                <span>
                  <User className="w-5 h-5 text-gray-500 " />
                </span>
                {toTitleCase(login.userId)}
              </p>
            </div>
            <div className="flex items-center"></div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-purple-500 mr-2" />
              <span className="text-gray-900 text-sm">
                {new Date(login.eventTimeStamp)
                  .toLocaleString([], {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                  .replace(" at ", ", ")}
              </span>
            </div>
            <Button
              variant="default"
              onClick={() => handleViewMap(login)}
              className="w-full h-8 items-center"
            >
              <MapPin className="w-4 h-4 mr-2" />
              View in Map
            </Button>
          </div>
        ))}
      </div>
      <>
        {/* Conditional rendering based on device type */}
        {isMobile ? (
          <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <DrawerContent className="max-h-[85vh] h-fit">
              <DrawerHeader className="pb-2">
                <DrawerTitle>Location details</DrawerTitle>
              </DrawerHeader>
              <div className="px-4 overflow-y-auto">
                {selectedLocation && (
                  <div className="my-4">
                    <div>
                      <iframe
                        src={selectedLocation.mapUrl}
                        width="100%"
                        height="250"
                        title="Map"
                        className="rounded-lg"
                      />
                    </div>
                    <div className="my-4 flex flex-col gap-2">
                      <div>
                        <h3 className="font-bold text-gray-900">Device Info</h3>
                        <p className="text-gray-600 text-sm">
                          {selectedLocation.login.metadata.browser},{" "}
                          {selectedLocation.login.metadata.device}
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Login Time</h3>
                        <p className="text-gray-600 text-sm">
                          {new Date(selectedLocation.login.eventTimeStamp)
                            .toLocaleString([], {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                            .replace(" at ", ", ")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DrawerContent>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm -z-10" />
          </Drawer>
        ) : (
          <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <SheetContent side="right" className="w-1/3">
              <SheetHeader>
                <SheetTitle>Login Activity Details</SheetTitle>
              </SheetHeader>
              <div className="my-4 mt-4">
                {selectedLocation && (
                  <div className="my-4">
                    <div>
                      <iframe
                        src={selectedLocation.mapUrl}
                        width="100%"
                        height="250"
                        title="Map"
                        className="rounded-lg"
                      />
                    </div>
                    <div className="my-4 flex flex-col gap-2">
                      <div>
                        <h3 className="font-bold text-gray-900">Device Info</h3>
                        <p className="text-gray-600 text-sm">
                          {selectedLocation.login.metadata.browser},{" "}
                          {selectedLocation.login.metadata.device}
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Login Time</h3>
                        <p className="text-gray-600 text-sm">
                          {new Date(selectedLocation.login.eventTimeStamp)
                            .toLocaleString([], {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                            .replace(" at ", ", ")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        )}
      </>
    </>
  );
}
